import { Grid2 } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import firebaseService from "firebase_service/firebaseService";
import ReactFlagsSelect from "react-flags-select";
import ReactCountryFlag from "react-country-flag";
import EditableProfile from "components/Widgets/EditableUser";
import clock_profile from "assets/img/klydoclock_profile.png";
import { FaEdit } from "react-icons/fa";
import {
  labelValueState,
  statesList,
  valueLabelState,
} from "../../../../utils/util";
import Earth from "assets/img/Earth.png";
import anonymousPhoto from "assets/img/anonymous.png";
import { descriptionMaxLength } from "./Users";
import SocialNetworkEdit from "components/Widgets/SocialNetworkEdit";
import { Klydo, SocialNetworkName } from "Types";
import { Author } from "../../../../api/author";
import { CuratorLevel, UserType } from "../../../../api/admin";
import ConfirmationSwitch from "../../../ConfirmationSwitch";
import ConfirmationModal from "../../../ConfirmationModal";

import useUpdateUserMutation from "../hooks/useUpdateUserMutation";
import useFeaturedArtistMutation from "../../../../components/hooks/useFeaturedArtistMutation";
import { usePlansQuery } from "../../../Studio/hooks/usePlans";
import { PlanStatus } from "../../../../api/plan";
import DSSelect from "../../../system-design/DSSelect";
import DSMenuItem from "../../../system-design/DSMenuItem";
import useResetPasswordMutation from "../hooks/useResetPasswordMutation";
import useSetPasswordMutation from "../hooks/useSetPasswordMutation";
import useSignOutUserMutation from "../hooks/useSignOutUserMutation";
import useVerifyEmailMutation from "../hooks/useVerifyEmailMutation";
import useCreateCollaboratorMutation from "../hooks/useCreateCollaboratorMutation";
import useArtistKlydoStats from "../hooks/useArtistKlydoStats";
import useUserProperties from "../hooks/useUserProperties";
import useSetUserPropertiesMutation from "../hooks/useSetUserPropertiesMutation";
import UserClockManagement from "./UserClockManagement";
import {
  useEligibleAdmins,
  useSetAdminPhoneMutation,
} from "../../../AlertPolicies/hooks/useAlertPolicyUsers";
import DSAlert from "../../../system-design/DSAlert";
import DSBox from "../../../system-design/DSBox";
import DSButton from "../../../system-design/DSButton";
import DSCard from "../../../system-design/DSCard";
import DSCardContent from "../../../system-design/DSCardContent";
import DSCardHeader from "../../../system-design/DSCardHeader";
import DSCheckbox from "../../../system-design/DSCheckBox";
import DSCircularProgress from "../../../system-design/DSCircularProgress";
import DSContainer from "../../../system-design/DSContainer";
import DSDialog from "../../../system-design/DSDialog";
import DSDialogActions from "../../../system-design/DSDialogActions";
import DSDialogContent from "../../../system-design/DSDialogContent";
import DSDialogTitle from "../../../system-design/DSDialogTitle";
import DSIconButton from "../../../system-design/DSIconButton";
import DSTextField from "../../../system-design/DSTextField";
import DSTypography from "../../../system-design/DSTypography";
import { sectionCardSx } from "../../../system-design/sectionCardSx";

const sectionTitleSx = {
  fontSize: 12,
  fontFamily: "Gilroy-SemiBold",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: "surface.accentText",
};

const sectionTitleBarSx = {
  width: 28,
  height: 2,
  bgcolor: "primary.main",
  borderRadius: 2,
  mt: 0.75,
  mx: 2,
};

const curatorLevelLabels: Record<CuratorLevel, string> = {
  [CuratorLevel.KLYDO_CURATOR]: "Klydo Curator",
  [CuratorLevel.SENIOR_CURATOR]: "Senior Curator",
  [CuratorLevel.MASTER_CURATOR]: "Master Curator",
};

// Signing the studio contract provisions the CollabPay collaborator in the
// background, and during a CollabPay outage its retries run for up to ~8
// minutes. A manual create in that window opens a second collaborator, and
// duplicates strand payouts, so the button waits this long after signing.
const COLLABORATOR_PROVISIONING_WINDOW_MS = 10 * 60 * 1000;

function UserModal(props: {
  row: Author;
  setShowModal: (b: boolean) => void;
  klydos: Array<Klydo>;
}) {
  const setShowModal = props.setShowModal;
  const user = props.row;
  const [featuredStatus, setFeaturedStatus] = useState<boolean>(
    user.featured || false,
  );
  const [manualPayout, setManualPayout] = useState<boolean>(
    user.manualPayout || false,
  );
  const [ignoreAffiliatePayments, setIgnoreAffiliatePayments] =
    useState<boolean>(user.metadata?.ignoreAffiliatePayments ?? false);
  const [defaultPlanId, setDefaultPlanId] = useState<string | null>(
    user.metadata?.defaultPlanId ?? null,
  );
  const [curatorLevel, setCuratorLevel] = useState<CuratorLevel | null>(
    user.curatorLevel ?? null,
  );
  const [curatorLevelError, setCuratorLevelError] = useState<string | null>(
    null,
  );
  const { data: plans } = usePlansQuery();
  const currentDefaultPlan = plans?.find((p) => p.id === defaultPlanId);
  const availablePlans = [
    ...(plans?.filter((p) => p.status !== PlanStatus.Deleted) ?? []),
    ...(currentDefaultPlan && currentDefaultPlan.status === PlanStatus.Deleted
      ? [currentDefaultPlan]
      : []),
  ];
  const { mutateAsync: updateUser } = useUpdateUserMutation();
  const { mutateAsync: toggleFeatured, isPending: isTogglingFeatured } =
    useFeaturedArtistMutation({
      onSuccess: () => {
        setFeaturedStatus(!featuredStatus);
      },
    });
  const [selectCountry, setSelectCountry] = useState(false);
  const [copiedUserId, setCopiedUserId] = useState(false);
  const [country, setCountry] = useState(user.country ?? "EA");
  const state = user.state ?? "AL";
  const [photoUrl, setPhotoUrl] = useState<string>(
    user.photoUrl ?? anonymousPhoto,
  );

  const [isArtistPageHidden, setIsArtistPageHidden] = useState<boolean>(
    user.metadata?.isArtistPageHidden ?? false,
  );
  const [phoneError, setPhoneError] = useState<string | null>(null);
  // Admin phones live in the notify recipients (Firestore), not on the user.
  const { data: eligibleAdmins } = useEligibleAdmins();
  const adminPhone =
    eligibleAdmins?.find((admin) => admin.id === user.id)?.phoneNumber ?? "";
  const { mutate: setAdminPhone } = useSetAdminPhoneMutation();

  const { data: userProperties } = useUserProperties(user.id);
  const [isB2B, setIsB2B] = useState<boolean>(false);
  useEffect(() => {
    if (userProperties) {
      setIsB2B(userProperties.b2b);
    }
  }, [userProperties]);
  const { mutateAsync: setUserProperties } = useSetUserPropertiesMutation();
  const queryClient = useQueryClient();
  // The level now lives in user-properties. Until the API serves it from there
  // (and the old field is removed from the user doc) fall back to the user's.
  useEffect(() => {
    if (userProperties) {
      setCuratorLevel(userProperties.curatorLevel ?? user.curatorLevel ?? null);
    }
  }, [userProperties, user.curatorLevel]);

  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [generatedResetLink, setGeneratedResetLink] = useState<string | null>(
    null,
  );
  const [resetLinkError, setResetLinkError] = useState<string | null>(null);
  const [showSignOutModal, setShowSignOutModal] = useState(false);

  const [showVerifyEmailModal, setShowVerifyEmailModal] = useState(false);
  const [collaboratorId, setCollaboratorId] = useState<string | null>(
    user.metadata?.collaboratorId ?? null,
  );
  const [showCreateCollaboratorModal, setShowCreateCollaboratorModal] =
    useState(false);
  const [showSetPasswordDialog, setShowSetPasswordDialog] = useState(false);
  const [showSetPasswordConfirmModal, setShowSetPasswordConfirmModal] =
    useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [setPasswordError, setSetPasswordError] = useState<string | null>(null);

  const passwordValidationError =
    newPassword.length > 0 && newPassword.length < 6
      ? "Password must be at least 6 characters long"
      : confirmNewPassword.length > 0 && newPassword !== confirmNewPassword
        ? "Passwords do not match"
        : null;
  const canSubmitNewPassword =
    newPassword.length >= 6 && newPassword === confirmNewPassword;

  const closeSetPasswordDialog = () => {
    setShowSetPasswordDialog(false);
    setNewPassword("");
    setConfirmNewPassword("");
    setSetPasswordError(null);
  };

  const { data: klydoStats, isLoading: isLoadingStats } = useArtistKlydoStats(
    user.id,
  );

  const hasKlydos = (klydoStats?.totalKlydos ?? 0) > 0;

  const { mutateAsync: resetPassword, isPending: isResettingPassword } =
    useResetPasswordMutation({
      onSuccess: (data) => {
        setShowResetPasswordModal(false);
        setGeneratedResetLink(data.link);
        setResetLinkError(null);
      },
      onError: (error) => {
        console.error("Failed to generate reset link:", error);
        setResetLinkError(error.message || "Failed to generate reset link");
        setGeneratedResetLink(null);
      },
    });

  const {
    mutateAsync: setUserPassword,
    isPending: isSettingPassword,
    isSuccess: isSetPasswordSuccess,
    reset: resetSetPassword,
  } = useSetPasswordMutation({
    onSuccess: () => {
      closeSetPasswordDialog();
    },
    onError: (error) => {
      setSetPasswordError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to set password",
      );
    },
  });

  const {
    mutateAsync: verifyUserEmail,
    isPending: isVerifyingEmail,
    isSuccess: isVerifyEmailSuccess,
    isError: isVerifyEmailError,
    error: verifyEmailError,
    reset: resetVerifyEmail,
  } = useVerifyEmailMutation({
    onSuccess: () => {
      setShowVerifyEmailModal(false);
    },
    onError: () => {
      setShowVerifyEmailModal(false);
    },
  });

  const {
    mutateAsync: createCollaborator,
    isPending: isCreatingCollaborator,
    isError: isCreateCollaboratorError,
    error: createCollaboratorError,
    reset: resetCreateCollaborator,
  } = useCreateCollaboratorMutation({
    onSuccess: (data) => {
      setShowCreateCollaboratorModal(false);
      setCollaboratorId(data.collaboratorId);
    },
    onError: () => {
      setShowCreateCollaboratorModal(false);
    },
  });
  const collaboratorRetryAt = user.studioContract?.signedTimestamp
    ? user.studioContract.signedTimestamp + COLLABORATOR_PROVISIONING_WINDOW_MS
    : null;
  const isCollaboratorProvisioning =
    collaboratorRetryAt !== null && Date.now() < collaboratorRetryAt;

  const {
    mutateAsync: signOutUser,
    isPending: isSigningOut,
    isSuccess: isSignOutSuccess,
    isError: isSignOutError,
    error: signOutError,
    reset: resetSignOut,
  } = useSignOutUserMutation({
    onSuccess: () => {
      setShowSignOutModal(false);
    },
    onError: () => {
      setShowSignOutModal(false);
    },
  });

  return (
    <>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1.5,
          borderBottom: "1px solid",
          borderBottomColor: "divider",
        }}
      >
        <DSTypography variant="h5">{user.name}</DSTypography>
        <CloseIcon
          sx={{ mt: 2, cursor: "pointer" }}
          onClick={() => setShowModal(false)}
        />
      </DSBox>
      <DSDialogContent>
        <DSContainer>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <DSCard
                sx={[
                  sectionCardSx,
                  {
                    pb: 2,
                    height: "100%",
                  },
                ]}
              >
                <DSCardHeader
                  title="Profile"
                  slotProps={{
                    title: { variant: "h6", sx: sectionTitleSx },
                  }}
                  sx={{ mt: 0, pb: 0 }}
                />
                <DSBox sx={sectionTitleBarSx} />
                <DSCardContent>
                  <DSBox
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <DSBox
                      sx={{
                        width: 200,
                        height: 200,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                      }}
                    >
                      <DSBox sx={{ position: "absolute" }}>
                        <img
                          referrerPolicy="no-referrer"
                          style={{ borderRadius: "50%" }}
                          loading="eager"
                          height={150}
                          width={150}
                          alt="profile"
                          src={
                            user.role === "clock"
                              ? clock_profile
                              : photoUrl ||
                                "https://react.semantic-ui.com/images/wireframe/square-image.png"
                          }
                        />
                      </DSBox>
                      <DSBox sx={{ height: 15, width: 15 }} />
                      <input
                        id="user-photo"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const fr = new FileReader();
                          const fr2 = new FileReader();
                          fr.onload = function () {
                            setPhotoUrl(fr.result as string);
                          };
                          fr2.onload = function () {
                            setTimeout(() => {
                              const url =
                                "https://api.cloudinary.com/v1_1/KlydoClock/image/upload";
                              const formData = new FormData();
                              formData.append("file", file);
                              formData.append("folder", "profile");
                              formData.append("upload_preset", "profiles");
                              return fetch(url, {
                                method: "POST",
                                body: formData,
                              })
                                .then((response) => {
                                  return response.json();
                                })
                                .then((res) => {
                                  const img = res.secure_url;
                                  if (img) {
                                    if (
                                      file.type === "image/gif" ||
                                      (file.type === "image/webp" &&
                                        fr2
                                          .result!.toString()
                                          .indexOf("ANMF") !== -1)
                                    ) {
                                      updateUser(
                                        {
                                          id: user.id,
                                          animated: true,
                                          photoUrl: img,
                                        },
                                        {
                                          onSuccess: () => {
                                            firebaseService.updateUserAnimatedPhoto(
                                              img,
                                            );
                                          },
                                        },
                                      );
                                    } else {
                                      updateUser({
                                        id: user.id,
                                        photoUrl: img,
                                        animated: false,
                                      });
                                    }
                                  }
                                });
                            }, 0);
                          };

                          fr.readAsDataURL(file);
                          fr2.readAsText(file);
                        }}
                        type="file"
                        style={{
                          width: "100%",
                          height: "100%",
                          opacity: 0,
                        }}
                      />
                    </DSBox>
                    {user.role !== "clock" ? (
                      <>
                        <SocialNetworkEdit
                          list={user.socialMedia ?? []}
                          networks={Object.values(SocialNetworkName)}
                          onChange={(list) => {
                            updateUser({
                              id: user.id,
                              socialMedia: list,
                            });
                          }}
                        />
                        {selectCountry ? (
                          <DSBox
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <DSBox sx={{ width: 300 }}>
                              <ReactFlagsSelect
                                selected={country}
                                searchable
                                fullWidth={true}
                                onSelect={(code) => {
                                  setCountry(code);
                                  setSelectCountry(false);
                                  updateUser({
                                    id: user.id,
                                    country: code,
                                  });
                                }}
                              />
                            </DSBox>
                            <CloseIcon
                              sx={{ mt: 2, cursor: "pointer" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectCountry(false);
                              }}
                            />
                          </DSBox>
                        ) : (
                          <DSBox
                            sx={{
                              position: "relative",
                              width: 55,
                              height: 55,
                            }}
                          >
                            {country === "EA" ? (
                              <img
                                src={Earth}
                                title="Earth"
                                style={{
                                  position: "relative",
                                  width: 55,
                                  height: 55,
                                }}
                                alt="Earth"
                              />
                            ) : (
                              <ReactCountryFlag
                                svg
                                countryCode={country}
                                style={{ width: 55, height: 55 }}
                              />
                            )}
                            <DSBox
                              sx={{
                                position: "absolute",
                                top: 33,
                                left: 47,
                              }}
                            >
                              <FaEdit
                                style={{
                                  backgroundColor: "white",
                                  cursor: "pointer",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectCountry(true);
                                }}
                              />
                            </DSBox>
                          </DSBox>
                        )}
                        {country === "US" ? (
                          <DSBox
                            sx={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <EditableProfile
                              type="select"
                              default={valueLabelState(state)}
                              onChange={(v) => {
                                updateUser({
                                  id: user.id,
                                  state: labelValueState(v),
                                });
                              }}
                              options={statesList.map((v) => v.label)}
                              style={{ fontSize: "2em" }}
                            />
                          </DSBox>
                        ) : null}
                        <DSBox
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            width: "50%",
                          }}
                        >
                          <EditableProfile
                            type={"short"}
                            default={user.name}
                            style={{ fontSize: "2.9em" }}
                            onChange={(v) =>
                              updateUser({ id: user.id, name: v })
                            }
                          />

                          <DSBox
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              alignContent: "center",
                              fontSize: "2.5em",
                            }}
                          >
                            <DSBox
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                              }}
                            >
                              <DSTypography
                                sx={{
                                  fontSize: "0.8em",
                                  margin: 0,
                                  marginRight: "4px",
                                  textAlign: "center",
                                }}
                              >
                                <EditableProfile
                                  type={"short"}
                                  default={user.email}
                                  style={{ fontSize: "1.5em" }}
                                  onChange={(v) =>
                                    updateUser({ id: user.id, email: v })
                                  }
                                />
                              </DSTypography>
                            </DSBox>
                          </DSBox>

                          {user.clock ? (
                            <DSBox
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignContent: "center",
                                fontSize: "0.8em",
                              }}
                            >
                              <DSBox
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                }}
                              >
                                <DSTypography
                                  sx={{
                                    fontSize: "0.8em",
                                    margin: 0,
                                    marginRight: "4px",
                                    textAlign: "center",
                                  }}
                                >
                                  clock: {user.clock}
                                </DSTypography>
                                <CloseIcon
                                  sx={{ cursor: "pointer" }}
                                  onClick={() =>
                                    firebaseService.removeAttachedClock(user.id)
                                  }
                                />
                              </DSBox>
                            </DSBox>
                          ) : null}
                          <DSTypography
                            sx={{
                              fontSize: "0.7em",
                              color: "text.secondary",
                              mt: 1,
                            }}
                          >
                            Description
                          </DSTypography>
                          <EditableProfile
                            active={!user.description}
                            type={"long"}
                            maxLength={descriptionMaxLength}
                            style={{ fontSize: "2em" }}
                            default={user.description ?? ""}
                            onChange={(v) => {
                              updateUser({
                                id: user.id,
                                description: v,
                              });
                            }}
                          />
                          <EditableProfile
                            type="select"
                            default={user.role || "editor"}
                            active={!user.role}
                            onChange={(v) => {
                              updateUser({ id: user.id, role: v });
                            }}
                            options={["editor", "admin", "viewer"]}
                            style={{ fontSize: "2em" }}
                          />
                          {(user.role === "admin" ||
                            user.role === "viewer") && (
                            <DSBox
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mt: 1,
                                fontSize: "0.8em",
                              }}
                            >
                              <DSTypography
                                sx={{
                                  fontSize: "0.9em",
                                  color: "text.secondary",
                                }}
                              >
                                GitHub username
                              </DSTypography>
                              <EditableProfile
                                type="short"
                                default={user.githubLogin ?? ""}
                                active={!user.githubLogin}
                                style={{ fontSize: "1.2em" }}
                                // Normalized and validated by the API.
                                onChange={(v) =>
                                  updateUser({ id: user.id, githubLogin: v })
                                }
                              />
                            </DSBox>
                          )}
                          {user.role === "admin" && (
                            <DSBox
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                mt: 1,
                              }}
                            >
                              <DSTypography
                                sx={{
                                  fontSize: "0.7em",
                                  color: "text.secondary",
                                }}
                              >
                                Admin phone (WhatsApp/SMS alerts)
                              </DSTypography>
                              {eligibleAdmins && (
                                <EditableProfile
                                  type="short"
                                  default={adminPhone}
                                  active={!adminPhone}
                                  onChange={(v) => {
                                    const trimmed = v.trim();
                                    if (
                                      trimmed &&
                                      !/^\+[1-9]\d{7,14}$/.test(trimmed)
                                    ) {
                                      setPhoneError(
                                        "Enter a valid number in E.164 format, e.g. +972501234567",
                                      );
                                      return;
                                    }
                                    setPhoneError(null);
                                    setAdminPhone({
                                      userId: user.id,
                                      phoneNumber: trimmed || null,
                                    });
                                  }}
                                  style={{ fontSize: "2em" }}
                                />
                              )}
                              {phoneError && (
                                <DSTypography
                                  sx={{
                                    fontSize: "0.6em",
                                    color: "error.main",
                                    mt: 0.5,
                                  }}
                                >
                                  {phoneError}
                                </DSTypography>
                              )}
                            </DSBox>
                          )}
                        </DSBox>
                        <DSBox
                          sx={{
                            marginTop: "15px",
                            fontSize: "1.2em",
                          }}
                        >
                          <strong>User ID:</strong>{" "}
                          <span style={{ fontFamily: "monospace" }}>
                            {user.id}
                          </span>
                          <DSIconButton
                            size="small"
                            title={copiedUserId ? "Copied" : "Copy user ID"}
                            onClick={() => {
                              navigator.clipboard
                                .writeText(user.id)
                                .then(() => {
                                  setCopiedUserId(true);
                                  setTimeout(
                                    () => setCopiedUserId(false),
                                    2000,
                                  );
                                })
                                .catch(console.log);
                            }}
                            sx={{ ml: 0.5 }}
                          >
                            {copiedUserId ? (
                              <CheckIcon sx={{ fontSize: 16 }} />
                            ) : (
                              <ContentCopyIcon sx={{ fontSize: 16 }} />
                            )}
                          </DSIconButton>
                        </DSBox>
                        <DSBox
                          sx={{
                            marginTop: "15px",
                            fontSize: "1.2em",
                          }}
                        >
                          <strong>Studio Contract:</strong>{" "}
                          {user.studioContract?.accepted
                            ? `Signed on ${new Date(
                                user.studioContract.signedTimestamp!,
                              ).toLocaleString("en-GB")}`
                            : "Didn't sign studio contract"}
                        </DSBox>
                        <DSBox
                          sx={{
                            marginTop: "15px",
                            fontSize: "1.2em",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            flexWrap: "wrap",
                            justifyContent: "center",
                          }}
                        >
                          <span>
                            <strong>CollabPay Collaborator:</strong>{" "}
                            {collaboratorId ?? "None"}
                          </span>
                          {!collaboratorId && (
                            <DSButton
                              size="small"
                              variant="contained"
                              disabled={
                                isCreatingCollaborator ||
                                isCollaboratorProvisioning
                              }
                              onClick={() =>
                                setShowCreateCollaboratorModal(true)
                              }
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              {isCreatingCollaborator ? (
                                <DSCircularProgress size={16} color="inherit" />
                              ) : null}
                              Create Collaborator
                            </DSButton>
                          )}
                        </DSBox>
                        {!collaboratorId && (
                          <DSAlert
                            severity="warning"
                            sx={{ marginTop: "15px" }}
                          >
                            Without a CollabPay collaborator the marketplace
                            publication pass skips every collection this user
                            owns.
                          </DSAlert>
                        )}
                        {!collaboratorId && isCollaboratorProvisioning && (
                          <DSAlert severity="info" sx={{ marginTop: "15px" }}>
                            This user just signed the studio contract, and the
                            collaborator is still being created in the
                            background. Reopen this user after{" "}
                            {new Date(collaboratorRetryAt!).toLocaleTimeString(
                              "en-GB",
                            )}{" "}
                            to check it or create it manually.
                          </DSAlert>
                        )}
                        {isCreateCollaboratorError && (
                          <DSAlert
                            severity="error"
                            onClose={resetCreateCollaborator}
                            sx={{ marginTop: "15px" }}
                          >
                            {createCollaboratorError?.response?.data?.message ||
                              createCollaboratorError?.message ||
                              "Failed to create CollabPay collaborator"}
                          </DSAlert>
                        )}
                        <DSBox
                          sx={{
                            marginTop: "15px",
                            display: "flex",
                            justifyContent: "center",
                            gap: "8px",
                          }}
                        >
                          <DSButton
                            variant="contained"
                            color="error"
                            disabled={isResettingPassword}
                            onClick={() => setShowResetPasswordModal(true)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            {isResettingPassword ? (
                              <DSCircularProgress size={16} color="inherit" />
                            ) : null}
                            Generate Reset Link
                          </DSButton>
                          <DSButton
                            variant="contained"
                            disabled={isSettingPassword}
                            onClick={() => setShowSetPasswordDialog(true)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            {isSettingPassword ? (
                              <DSCircularProgress size={16} color="inherit" />
                            ) : null}
                            Set Password
                          </DSButton>
                          <DSButton
                            variant="contained"
                            color="success"
                            disabled={isVerifyingEmail}
                            onClick={() => setShowVerifyEmailModal(true)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            {isVerifyingEmail ? (
                              <DSCircularProgress size={16} color="inherit" />
                            ) : null}
                            Verify Email
                          </DSButton>
                          <DSButton
                            variant="contained"
                            color="warning"
                            disabled={isSigningOut}
                            onClick={() => setShowSignOutModal(true)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            {isSigningOut ? (
                              <DSCircularProgress size={16} color="inherit" />
                            ) : null}
                            Sign Out User
                          </DSButton>
                        </DSBox>
                        {resetLinkError && (
                          <DSBox
                            sx={{
                              marginTop: "15px",
                              padding: "10px",
                              backgroundColor: "rgba(244,67,54,0.12)",
                              color: "error.light",
                              border: "1px solid rgba(244,67,54,0.3)",
                              borderRadius: "12px",
                              fontSize: "14px",
                              position: "relative",
                            }}
                          >
                            <strong>Error:</strong> {resetLinkError}
                            <DSButton
                              onClick={() => setResetLinkError(null)}
                              sx={{
                                position: "absolute",
                                top: "5px",
                                right: "5px",
                                minWidth: "auto",
                                color: "inherit",
                              }}
                            >
                              ×
                            </DSButton>
                          </DSBox>
                        )}
                        {isSignOutError && (
                          <DSAlert
                            severity="error"
                            onClose={resetSignOut}
                            sx={{ marginTop: "15px" }}
                          >
                            {signOutError?.response?.data?.message ||
                              signOutError?.message ||
                              "Failed to sign out user"}
                          </DSAlert>
                        )}
                        {isSignOutSuccess && (
                          <DSAlert
                            severity="success"
                            onClose={resetSignOut}
                            sx={{ marginTop: "15px" }}
                          >
                            User has been signed out successfully.
                          </DSAlert>
                        )}
                        {isSetPasswordSuccess && (
                          <DSAlert
                            severity="success"
                            onClose={resetSetPassword}
                            sx={{ marginTop: "15px" }}
                          >
                            Password has been changed and the user was signed
                            out of all sessions.
                          </DSAlert>
                        )}
                        {isVerifyEmailError && (
                          <DSAlert
                            severity="error"
                            onClose={resetVerifyEmail}
                            sx={{ marginTop: "15px" }}
                          >
                            {verifyEmailError?.response?.data?.message ||
                              verifyEmailError?.message ||
                              "Failed to verify email"}
                          </DSAlert>
                        )}
                        {isVerifyEmailSuccess && (
                          <DSAlert
                            severity="success"
                            onClose={resetVerifyEmail}
                            sx={{ marginTop: "15px" }}
                          >
                            Email has been marked as verified.
                          </DSAlert>
                        )}
                        {generatedResetLink && (
                          <DSBox
                            sx={{
                              marginTop: "15px",
                              padding: "10px",
                              backgroundColor: "rgba(102,187,106,0.12)",
                              color: "success.light",
                              border: "1px solid rgba(102,187,106,0.3)",
                              borderRadius: "12px",
                              fontSize: "14px",
                              position: "relative",
                            }}
                          >
                            <strong>Reset Link Generated:</strong>
                            <DSBox
                              sx={{
                                marginTop: "8px",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <DSTextField
                                type="text"
                                value={generatedResetLink}
                                size="small"
                                InputProps={{
                                  readOnly: true,
                                }}
                                sx={{
                                  flex: 1,
                                  "& .MuiInputBase-input": {
                                    fontSize: "12px",
                                  },
                                }}
                              />
                              <DSButton
                                size="small"
                                variant="outlined"
                                color="success"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    generatedResetLink,
                                  );
                                }}
                              >
                                Copy
                              </DSButton>
                            </DSBox>
                            <DSButton
                              onClick={() => setGeneratedResetLink(null)}
                              sx={{
                                position: "absolute",
                                top: "10px",
                                right: "10px",
                                minWidth: "auto",
                                color: "inherit",
                              }}
                            >
                              ×
                            </DSButton>
                          </DSBox>
                        )}
                      </>
                    ) : (
                      <DSTypography>Clock</DSTypography>
                    )}
                  </DSBox>
                </DSCardContent>
              </DSCard>
            </Grid2>

            {isLoadingStats ? (
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <DSBox
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <DSCircularProgress size="small" />
                </DSBox>
              </Grid2>
            ) : hasKlydos ? (
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <DSCard
                  sx={[
                    sectionCardSx,
                    {
                      height: "100%",
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                    },
                  ]}
                >
                  <DSCardHeader
                    title="Klydo stats"
                    slotProps={{ title: { variant: "h6", sx: sectionTitleSx } }}
                    sx={{ mt: 0, pb: 0 }}
                  />
                  <DSBox sx={sectionTitleBarSx} />
                  <DSCardContent>
                    <DSTypography variant="h6">
                      <strong>Number of public klydos: </strong>
                      {klydoStats?.totalPublic ?? 0}
                    </DSTypography>
                    <DSTypography variant="h6">
                      <strong>Total view time: </strong>
                      {klydoStats?.totalTime ?? 0}
                    </DSTypography>
                    <DSTypography variant="h6">
                      <strong>Total favorites: </strong>
                      {klydoStats?.totalFavorites ?? 0}
                    </DSTypography>
                    {user.type == UserType.ARTIST && (
                      <DSTypography
                        variant="h6"
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <strong>Featured: </strong>
                        <ConfirmationSwitch
                          status={featuredStatus}
                          action={() => {
                            toggleFeatured(user.id);
                          }}
                          onMessage={`Are you sure you want to make ${user.name} a featured artist?`}
                          offMessage={`Are you sure you want to remove ${user.name} from featured artists?`}
                          disabledReason={
                            isTogglingFeatured ? "Processing..." : undefined
                          }
                        />
                      </DSTypography>
                    )}
                    {user.type == UserType.ARTIST && (
                      <DSTypography
                        variant="h6"
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <strong>Manual Payout: </strong>
                        <ConfirmationSwitch
                          status={manualPayout}
                          action={() => {
                            updateUser(
                              {
                                id: user.id,
                                manualPayout: !manualPayout,
                              },
                              {
                                onSuccess: () => {
                                  setManualPayout(!manualPayout);
                                },
                              },
                            );
                          }}
                          onMessage={`Are you sure you want to enable manual payout for ${user.name}?`}
                          offMessage={`Are you sure you want to disable manual payout for ${user.name}?`}
                        />
                      </DSTypography>
                    )}
                    {user.type == UserType.ARTIST && (
                      <DSTypography
                        variant="h6"
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <strong>Ignore Edition Payments: </strong>
                        <ConfirmationSwitch
                          status={ignoreAffiliatePayments}
                          action={() => {
                            updateUser(
                              {
                                id: user.id,
                                metadata: {
                                  ...user.metadata,
                                  ignoreAffiliatePayments:
                                    !ignoreAffiliatePayments,
                                },
                              },
                              {
                                onSuccess: () => {
                                  setIgnoreAffiliatePayments(
                                    !ignoreAffiliatePayments,
                                  );
                                },
                              },
                            );
                          }}
                          onMessage={`Opt ${user.name} out of affiliate payouts? The payout cron will skip their collections — affiliate orders stay unpaid while the flag is on.`}
                          offMessage={`Opt ${user.name} back into affiliate payouts? Any unpaid affiliate orders for their collections will be paid on the next daily payout run.`}
                        />
                      </DSTypography>
                    )}
                    {user.type == UserType.ARTIST && (
                      <DSBox sx={{ mt: 1 }}>
                        <DSTypography variant="h6" sx={{ mb: 0.5 }}>
                          <strong>Default Plan:</strong>
                        </DSTypography>
                        <DSSelect
                          size="small"
                          fullWidth
                          value={defaultPlanId ?? ""}
                          onChange={(e) => {
                            const val = e.target.value || null;
                            setDefaultPlanId(val);
                            updateUser({
                              id: user.id,
                              metadata: {
                                ...user.metadata,
                                defaultPlanId: val,
                              },
                            });
                          }}
                          displayEmpty
                        >
                          <DSMenuItem value="">
                            <em>None (artist selects)</em>
                          </DSMenuItem>
                          {availablePlans.map((plan) => (
                            <DSMenuItem key={plan.id} value={plan.id}>
                              {plan.title ? `${plan.title} — ` : ""}$
                              {plan.price} ·{" "}
                              {plan.upfrontAmount > 0
                                ? `$${plan.upfrontAmount} upfront`
                                : "No upfront"}{" "}
                              ·{" "}
                              {plan.revSharePercentage > 0
                                ? `${plan.revSharePercentage}% rev share`
                                : "No rev share"}
                              {plan.isAdvanced ? " (Advanced)" : ""}
                              {plan.status === PlanStatus.Unlisted
                                ? " [Unlisted]"
                                : ""}
                            </DSMenuItem>
                          ))}
                        </DSSelect>
                      </DSBox>
                    )}
                    {user.type == UserType.ARTIST && (
                      <DSBox sx={{ mt: 1 }}>
                        <DSTypography variant="h6" sx={{ mb: 0.5 }}>
                          <strong>Curator Level:</strong>
                        </DSTypography>
                        <DSSelect
                          size="small"
                          fullWidth
                          value={curatorLevel ?? ""}
                          onChange={(e) => {
                            const val = (e.target.value ||
                              null) as CuratorLevel | null;
                            const previousLevel = curatorLevel;
                            setCuratorLevel(val);
                            setCuratorLevelError(null);
                            updateUser({
                              id: user.id,
                              curatorLevel: val,
                            })
                              .then(() =>
                                queryClient.invalidateQueries({
                                  queryKey: ["userProperties", user.id],
                                }),
                              )
                              .catch((error) => {
                                setCuratorLevel(previousLevel);
                                setCuratorLevelError(
                                  error?.response?.data?.message ||
                                    error?.message ||
                                    "Failed to update curator level",
                                );
                              });
                          }}
                          displayEmpty
                        >
                          <DSMenuItem value="">
                            <em>Auto</em>
                          </DSMenuItem>
                          {Object.values(CuratorLevel).map((level) => (
                            <DSMenuItem key={level} value={level}>
                              {curatorLevelLabels[level]}
                            </DSMenuItem>
                          ))}
                        </DSSelect>
                        {curatorLevelError && (
                          <DSAlert
                            severity="error"
                            onClose={() => setCuratorLevelError(null)}
                            sx={{ marginTop: "15px" }}
                          >
                            {curatorLevelError}
                          </DSAlert>
                        )}
                      </DSBox>
                    )}
                  </DSCardContent>
                </DSCard>
              </Grid2>
            ) : (
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <UserClockManagement userId={user.id} />
              </Grid2>
            )}
            {user.type == UserType.ARTIST && (
              <Grid2 size={{ xs: 12 }}>
                <DSCard sx={sectionCardSx}>
                  <DSCardHeader
                    title="Artist page"
                    slotProps={{ title: { variant: "h6", sx: sectionTitleSx } }}
                    sx={{ mt: 0, pb: 0 }}
                  />
                  <DSBox sx={sectionTitleBarSx} />
                  <DSCardContent>
                    <DSBox sx={{ display: "flex", alignItems: "center" }}>
                      <DSCheckbox
                        checked={isArtistPageHidden}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setIsArtistPageHidden(checked);
                          updateUser({
                            id: user.id,
                            metadata: {
                              ...user.metadata,
                              isArtistPageHidden: checked,
                            },
                          });
                        }}
                      />
                      <DSTypography>
                        Hide artist from Shopify's artists page
                      </DSTypography>
                    </DSBox>
                  </DSCardContent>
                </DSCard>
              </Grid2>
            )}
            <Grid2 size={{ xs: 12 }}>
              <DSCard sx={sectionCardSx}>
                <DSCardHeader
                  title="Account settings"
                  slotProps={{ title: { variant: "h6", sx: sectionTitleSx } }}
                  sx={{ mt: 0, pb: 0 }}
                />
                <DSBox sx={sectionTitleBarSx} />
                <DSCardContent>
                  <DSBox sx={{ display: "flex", alignItems: "center" }}>
                    <DSCheckbox
                      checked={isB2B}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setIsB2B(checked);
                        setUserProperties({ userId: user.id, b2b: checked });
                      }}
                    />
                    <DSTypography>B2B user</DSTypography>
                  </DSBox>
                </DSCardContent>
              </DSCard>
            </Grid2>
          </Grid2>
          {hasKlydos && (
            <Grid2 size={{ xs: 12, sm: 6 }} style={{ marginTop: "20px" }}>
              <UserClockManagement userId={user.id} />
            </Grid2>
          )}
          <hr />
        </DSContainer>
      </DSDialogContent>
      <DSDialogActions sx={{ justifyContent: "flex-end" }}>
        <DSButton variant="text" onClick={() => setShowModal(false)}>
          OK
        </DSButton>
      </DSDialogActions>

      <ConfirmationModal
        open={showResetPasswordModal}
        message={`A password reset link will be generated for ${user.email}. Are you sure you want to proceed?`}
        onClose={() => setShowResetPasswordModal(false)}
        onConfirm={() => resetPassword(user.id)}
      />

      <ConfirmationModal
        open={showSignOutModal}
        message={`This will sign out ${user.email} from their account. Are you sure you want to proceed?`}
        onClose={() => setShowSignOutModal(false)}
        onConfirm={() => {
          setShowSignOutModal(false);
          signOutUser(user.id);
        }}
      />

      <DSDialog
        className="modal-medium"
        open={showSetPasswordDialog}
        onClose={closeSetPasswordDialog}
      >
        <DSDialogTitle>Set Password</DSDialogTitle>
        <DSDialogContent>
          <DSTypography sx={{ mb: 2 }}>
            Set a new password for <strong>{user.email}</strong>.
          </DSTypography>
          <DSBox
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              mt: 1,
            }}
          >
            <DSTextField
              type="password"
              label="New password"
              size="small"
              fullWidth
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <DSTextField
              type="password"
              label="Confirm new password"
              size="small"
              fullWidth
              autoComplete="new-password"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
            />
          </DSBox>
          {passwordValidationError && (
            <DSTypography sx={{ color: "error.main", mt: 1, fontSize: 14 }}>
              {passwordValidationError}
            </DSTypography>
          )}
          {setPasswordError && (
            <DSAlert
              severity="error"
              onClose={() => setSetPasswordError(null)}
              sx={{ mt: 2 }}
            >
              {setPasswordError}
            </DSAlert>
          )}
        </DSDialogContent>
        <DSDialogActions>
          <DSButton variant="text" onClick={closeSetPasswordDialog}>
            Cancel
          </DSButton>
          <DSButton
            variant="contained"
            color="error"
            disabled={!canSubmitNewPassword || isSettingPassword}
            onClick={() => setShowSetPasswordConfirmModal(true)}
            sx={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            {isSettingPassword ? (
              <DSCircularProgress size={16} color="inherit" />
            ) : null}
            Set Password
          </DSButton>
        </DSDialogActions>
      </DSDialog>

      <ConfirmationModal
        open={showCreateCollaboratorModal}
        message={`This will open a CollabPay collaborator for ${user.email} and link it to this user. Are you sure you want to proceed?`}
        onClose={() => setShowCreateCollaboratorModal(false)}
        onConfirm={() => {
          createCollaborator(user.id);
        }}
      />

      <ConfirmationModal
        open={showVerifyEmailModal}
        message={`This will mark ${user.email} as a verified email address in Firebase Auth. Are you sure you want to proceed?`}
        onClose={() => setShowVerifyEmailModal(false)}
        onConfirm={() => {
          verifyUserEmail(user.id);
        }}
      />

      <ConfirmationModal
        open={showSetPasswordConfirmModal}
        message={`This will immediately change the password for ${user.email} and sign them out of all active sessions. Are you sure you want to proceed?`}
        onClose={() => setShowSetPasswordConfirmModal(false)}
        onConfirm={() => {
          setShowSetPasswordConfirmModal(false);
          setUserPassword({ userId: user.id, password: newPassword });
        }}
      />
    </>
  );
}

export default UserModal;
